import { useAudioPlayer, useAudioPlayerStatus } from 'expo-audio';
import { useVideoPlayer } from 'expo-video';
import { createContext, useContext, useEffect, useState } from 'react';

const MediaPlayerContext = createContext();

export const useMediaPlayer = () => {
  const context = useContext(MediaPlayerContext);
  if (!context) {
    throw new Error('useMediaPlayer must be used within a MediaPlayerProvider');
  }
  return context;
};

export const MediaPlayerProvider = ({ children }) => {
  const [currentMedia, setCurrentMedia] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [error, setError] = useState(null);

  // Determine media type based on file extension
  const getMediaType = (mediaAsset) => {
    if (!mediaAsset) return null;
    
    const isVideo = mediaAsset.toLowerCase().endsWith('.mp4') || 
                    mediaAsset.toLowerCase().endsWith('.mov') ||
                    mediaAsset.toLowerCase().endsWith('.m4v');
    
    const isAudio = mediaAsset.toLowerCase().endsWith('.mp3') || 
                    mediaAsset.toLowerCase().endsWith('.m4a') ||
                    mediaAsset.toLowerCase().endsWith('.wav');
    
    if (isVideo) return 'video';
    if (isAudio) return 'audio';
    return null;
  };

  // Map asset filenames to require statements
  const getMediaSource = (mediaAsset) => {
    const mediaMap = {
      'Authority.mp3': require('../../assets/Authority.mp3'),
    };
    return mediaMap[mediaAsset];
  };

  // Initialize players only when we have media
  const mediaType = getMediaType(currentMedia);
  const mediaSource = currentMedia ? getMediaSource(currentMedia) : null;
  
  const audioPlayer = useAudioPlayer(mediaType === 'audio' ? mediaSource : null);
  const audioStatus = useAudioPlayerStatus(audioPlayer);
  
  const videoPlayer = useVideoPlayer(mediaType === 'video' ? mediaSource : null, (player) => {
    if (player) {
      player.loop = false;
    }
  });

  // Functions to control media playback
  const playMedia = (mediaAsset) => {
    try {
      setError(null);
      setCurrentMedia(mediaAsset);
      setIsModalVisible(true);
      
      // Auto-play when media is set (after players are initialized)
      // We'll handle this in useEffect below
    } catch (err) {
      console.error('Error setting up media:', err);
      setError('Error setting up media');
    }
  };

  const pauseMedia = () => {
    try {
      setError(null);
      if (mediaType === 'audio') {
        audioPlayer.pause();
      } else if (mediaType === 'video' && videoPlayer) {
        videoPlayer.pause();
      }
    } catch (err) {
      console.error('Error pausing media:', err);
      setError('Error pausing media');
    }
  };

  const resumeMedia = () => {
    try {
      setError(null);
      if (mediaType === 'audio') {
        audioPlayer.play();
      } else if (mediaType === 'video' && videoPlayer) {
        videoPlayer.play();
      }
    } catch (err) {
      console.error('Error resuming media:', err);
      setError('Error resuming media');
    }
  };

  const stopMedia = () => {
    try {
      setError(null);
      if (mediaType === 'audio') {
        audioPlayer.pause();
        // Note: expo-audio doesn't have a direct stop method, pause is closest
      } else if (mediaType === 'video' && videoPlayer) {
        videoPlayer.pause();
        // Reset to beginning if possible
        if (videoPlayer.currentTime !== undefined) {
          videoPlayer.seekBy(-videoPlayer.currentTime);
        }
      }
      setCurrentMedia(null);
      setIsModalVisible(false);
    } catch (err) {
      console.error('Error stopping media:', err);
      setError('Error stopping media');
    }
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const hideModal = () => {
    setIsModalVisible(false);
  };

  // Auto-play when media changes and players are ready
  useEffect(() => {
    if (currentMedia && mediaSource) {
      const autoPlay = async () => {
        try {
          if (mediaType === 'audio' && audioPlayer) {
            audioPlayer.play();
          } else if (mediaType === 'video' && videoPlayer) {
            videoPlayer.play();
          }
        } catch (err) {
          console.error('Error auto-playing media:', err);
          setError('Error playing media');
        }
      };
      
      // Small delay to ensure players are ready
      const timer = setTimeout(autoPlay, 100);
      return () => clearTimeout(timer);
    }
  }, [currentMedia, mediaSource, mediaType, audioPlayer, videoPlayer]);

  const contextValue = {
    // State
    currentMedia,
    mediaType,
    isModalVisible,
    error,
    
    // Audio player state (when audio is playing)
    audioPlayer: mediaType === 'audio' ? audioPlayer : null,
    audioStatus: mediaType === 'audio' ? audioStatus : null,
    
    // Video player state (when video is playing)
    videoPlayer: mediaType === 'video' ? videoPlayer : null,
    
    // Actions
    playMedia,
    pauseMedia,
    resumeMedia,
    stopMedia,
    showModal,
    hideModal,
    
    // Helper functions
    getMediaSource,
    getMediaType,
  };

  return (
    <MediaPlayerContext.Provider value={contextValue}>
      {children}
    </MediaPlayerContext.Provider>
  );
};